<template>
  <section class="sentence-notebook">
    <div class="sentence-toolbar">
      <label class="sentence-search">
        <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.75" /><path d="M10.5 10.5L14 14" /></svg>
        <input v-model.trim="search" type="search" :placeholder="copy.searchPlaceholder">
      </label>
      <span class="sentence-count">{{ copy.count(filteredEntries.length) }}</span>
    </div>

    <div v-if="loading" class="sentence-state">{{ copy.loading }}</div>
    <div v-else-if="error" class="sentence-state error">
      <span>{{ error }}</span>
      <button type="button" @click="loadEntries">{{ copy.retry }}</button>
    </div>
    <div v-else-if="entries.length === 0" class="sentence-state">{{ copy.empty }}</div>
    <div v-else-if="filteredEntries.length === 0" class="sentence-state">{{ copy.noSearchResult }}</div>

    <div v-else class="sentence-content">
      <div class="sentence-list" :aria-label="copy.listTitle">
        <button
          v-for="entry in filteredEntries"
          :key="entry.id"
          type="button"
          class="sentence-row"
          :class="{ selected: selectedEntry?.id === entry.id }"
          @click="selectEntry(entry)"
        >
          <span>{{ entry.sentence }}</span>
          <small v-if="entry.explanation">{{ entry.explanation.gist }}</small>
        </button>
      </div>

      <Transition name="detail-swap" mode="out-in">
        <article v-if="selectedEntry" :key="selectedEntry.id" class="sentence-detail">
          <header class="detail-head">
            <h2>{{ selectedEntry.sentence }}</h2>
            <div v-if="canOpen(selectedEntry)" class="source-row">
              <span class="source-name">{{ sourceLabel(selectedEntry) }}</span>
              <button type="button" class="open-source" :disabled="opening" @click="openSource(selectedEntry)">
                {{ opening ? copy.opening : copy.openSource }}
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8h8.5M8.5 4.5L12 8l-3.5 3.5" /></svg>
              </button>
            </div>
            <p v-if="actionError" class="action-error">{{ actionError }}</p>
          </header>

          <SentenceExplanationContent
            v-if="selectedEntry.explanation"
            :explanation="selectedEntry.explanation"
            :follow-ups="selectedEntry.followUps"
          />
          <p v-else class="explanation-missing">{{ copy.missingExplanation }}</p>

          <footer class="detail-meta">
            <time>{{ copy.savedAt }} · {{ formatDate(selectedEntry.savedAt) || copy.recentlySaved }}</time>
            <button type="button" class="delete-link" @click="showDeleteDialog = true">{{ copy.delete }}</button>
          </footer>
        </article>
      </Transition>
    </div>

    <DialogModal v-if="showDeleteDialog" :show="showDeleteDialog" :title="copy.deleteTitle" @close="closeDeleteDialog">
      <p class="delete-message">{{ copy.deleteMessage }}</p>
      <p class="delete-warning">{{ copy.deleteWarning }}</p>
      <template #buttons>
        <button class="dialog-action secondary" type="button" @click="closeDeleteDialog">{{ copy.cancel }}</button>
        <button class="dialog-action danger" type="button" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? copy.deleting : copy.confirmDelete }}
        </button>
      </template>
    </DialogModal>
  </section>
</template>

<script>
import { apiPost } from '@/composables/useApi'
import { canOpenStudySource, resolveStudySource, StudySourceMissingError, StudySourceNotReadyError } from '@/composables/studySource'
import { useViews } from '@/composables/views'
import { auth } from '@/firebase'
import DialogModal from '@/components/modals/DialogModal.vue'
import SentenceExplanationContent from '@/components/player/SentenceExplanationContent.vue'

const copyByLocale = {
  zh: {
    loading: '正在加载句子...', retry: '重试', empty: '还没有保存的句子解释。到字幕页点击“解释这句”开始。',
    noSearchResult: '没有匹配的句子。', searchPlaceholder: '搜索原句或解释', listTitle: '已保存的句子',
    savedAt: '加入于', recentlySaved: '刚刚', delete: '移出学习本', deleting: '移出中...',
    deleteTitle: '移出学习本', deleteMessage: '确定把这句话移出学习本吗？', deleteWarning: '解释仍会保留，回到原文再次打开这句即可重新加入。',
    cancel: '取消', confirmDelete: '移出', loginRequired: '请先登录再查看学习本。', count: (count) => `${count} 个句子`,
    missingExplanation: '这条是旧版解释，回到字幕里重新解释即可更新。',
    openSource: '回到原文', opening: '正在打开', sourceFrom: (name) => `来自《${name}》`, sourceUnknown: '来自你的练习',
    sourceMissing: '原文已被删除，无法打开。', openFailed: '暂时无法打开原文，请稍后重试。',
  },
  en: {
    loading: 'Loading sentences...', retry: 'Retry', empty: 'No saved explanations yet. Use “Explain this sentence” in the transcript.',
    noSearchResult: 'No matching sentences.', searchPlaceholder: 'Search sentences or explanations', listTitle: 'Saved sentences',
    savedAt: 'Added', recentlySaved: 'Recently', delete: 'Remove from notebook', deleting: 'Removing...',
    deleteTitle: 'Remove from Study notebook', deleteMessage: 'Remove this sentence from your Study notebook?', deleteWarning: 'The explanation is kept. Open the sentence again to add it back.',
    cancel: 'Cancel', confirmDelete: 'Remove', loginRequired: 'Log in to view your Study notebook.', count: (count) => `${count} sentences`,
    missingExplanation: 'This entry uses the old format. Explain it again from the transcript to update it.',
    openSource: 'Back to source', opening: 'Opening', sourceFrom: (name) => `From “${name}”`, sourceUnknown: 'From your practice',
    sourceMissing: 'The source has been deleted and can’t be opened.', openFailed: 'Couldn’t open the source. Try again later.',
  },
}

export default {
  name: 'SentenceNotebook',
  components: { DialogModal, SentenceExplanationContent },
  setup() {
    const { handleViewChange } = useViews()
    return { _handleViewChange: handleViewChange }
  },
  data() {
    return {
      loading: false,
      error: '',
      entries: [],
      search: '',
      selectedId: '',
      showDeleteDialog: false,
      deleting: false,
      opening: false,
      actionError: '',
    }
  },
  computed: {
    copy() { return this.$i18n?.locale === 'en' ? copyByLocale.en : copyByLocale.zh },
    filteredEntries() {
      const keyword = this.search.toLowerCase()
      if (!keyword) return this.entries
      return this.entries.filter((entry) => [
        entry.sentence,
        entry.explanation?.gist,
        ...(entry.explanation?.points || []).flatMap((point) => [point.original, point.note]),
        ...Object.values(entry.followUps || {}).flatMap((answer) => answer.items.flatMap((item) => [item.text, item.note])),
      ].some((value) => String(value || '').toLowerCase().includes(keyword)))
    },
    selectedEntry() {
      return this.filteredEntries.find((entry) => entry.id === this.selectedId) || this.filteredEntries[0] || null
    },
  },
  mounted() { this.loadEntries() },
  methods: {
    async loadEntries() {
      if (!auth.currentUser) { this.error = this.copy.loginRequired; return }
      this.loading = true
      this.error = ''
      try {
        const result = await apiPost('/api/sentence-explanations/list', { limit: 200 })
        this.entries = result.entries || []
      } catch (error) {
        this.error = error?.message || this.copy.loading
      } finally {
        this.loading = false
      }
    },
    selectEntry(entry) {
      this.selectedId = entry.id
      this.actionError = ''
    },
    entrySource(entry) {
      return {
        audioId: entry.lastAudioId,
        audioName: entry.lastAudioName,
        materialId: entry.lastMaterialId,
        materialBook: entry.lastMaterialBook,
      }
    },
    canOpen(entry) {
      return canOpenStudySource(this.entrySource(entry))
    },
    sourceLabel(entry) {
      return entry.lastAudioName ? this.copy.sourceFrom(entry.lastAudioName) : this.copy.sourceUnknown
    },
    async openSource(entry) {
      if (this.opening) return
      this.opening = true
      this.actionError = ''
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      try {
        const audio = await resolveStudySource(this.entrySource(entry), translate)
        this._handleViewChange('AudioPlayer', {
          ...audio,
          useSmartSplit: false,
          isDemo: false,
          focusSentence: { text: entry.sentence, index: entry.lastSubtitleIndex },
          returnView: 'VocabularyBook',
          returnParams: { notebookTab: 'sentences' },
        })
      } catch (error) {
        if (error instanceof StudySourceMissingError) this.actionError = this.copy.sourceMissing
        else if (error instanceof StudySourceNotReadyError) this.actionError = this.copy.openFailed
        else this.actionError = error?.message || this.copy.openFailed
      } finally {
        this.opening = false
      }
    },
    formatDate(value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
    },
    closeDeleteDialog() { if (!this.deleting) this.showDeleteDialog = false },
    async confirmDelete() {
      if (!this.selectedEntry || this.deleting) return
      const entryId = this.selectedEntry.id
      this.deleting = true
      try {
        await apiPost('/api/sentence-explanations/save', { id: entryId, saved: false })
        this.entries = this.entries.filter((entry) => entry.id !== entryId)
        this.showDeleteDialog = false
      } catch (error) {
        this.error = error?.message || 'Remove failed'
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.sentence-notebook { height: var(--workspace-fill-height); display: flex; flex-direction: column; min-height: 0; }
.sentence-toolbar { padding-bottom: var(--space-4); display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); }
.sentence-search { width: min(360px, 100%); height: 36px; display: flex; align-items: center; gap: var(--space-2); padding: 0 var(--space-3); background: var(--surface-panel); border: 1px solid var(--border-default); border-radius: var(--radius-md); }
.sentence-search:focus-within { border-color: var(--signal-active-strong); box-shadow: var(--focus-ring-brand); }
.sentence-search svg { width: 15px; height: 15px; flex: 0 0 auto; fill: none; stroke: var(--color-text-subtle); stroke-width: 1.6; stroke-linecap: round; }
.sentence-search input { width: 100%; border: 0; outline: 0; background: transparent; color: var(--color-text); font: inherit; font-size: 0.88rem; }
.sentence-search input::placeholder { color: var(--color-text-subtle); }
.sentence-count { color: var(--color-text-subtle); font-size: 0.8rem; }
.sentence-state { flex: 1; min-height: 240px; padding: var(--space-8); display: flex; align-items: center; justify-content: center; gap: var(--space-3); color: var(--ink-secondary); background: var(--surface-panel); border: 1px solid var(--border-default); border-radius: var(--radius-lg); text-align: center; }
.sentence-state.error { color: var(--color-danger); }
.sentence-state button { min-height: 34px; padding: 0 var(--space-3); color: var(--color-text); background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; cursor: pointer; font: inherit; }

.sentence-content { flex: 1; min-height: 0; overflow: hidden; display: grid; grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.45fr); background: var(--surface-panel); border: 1px solid var(--border-default); border-radius: var(--radius-lg); }
.sentence-list { min-height: 0; padding: var(--space-3); overflow-y: auto; border-right: 1px solid var(--color-border); scrollbar-width: thin; }
.sentence-row { width: 100%; padding: var(--space-3); display: grid; gap: 4px; color: var(--color-text); background: transparent; border: 0; border-radius: 8px; cursor: pointer; font: inherit; text-align: left; transition: background 160ms ease; }
.sentence-row + .sentence-row { margin-top: 2px; }
.sentence-row:hover { background: var(--color-surface-muted); }
.sentence-row.selected { background: var(--color-brand-50); }
.sentence-row span { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; font-size: 0.92rem; line-height: 1.5; }
.sentence-row small { overflow: hidden; color: var(--color-text-subtle); font-size: 0.78rem; line-height: 1.5; text-overflow: ellipsis; white-space: nowrap; }

.sentence-detail { min-height: 0; padding: var(--space-8) 0 var(--space-6) var(--space-10); display: flex; flex-direction: column; gap: var(--space-8); overflow-y: auto; scrollbar-width: thin; }
.sentence-detail > * { max-width: 680px; }
.detail-head { display: grid; gap: var(--space-4); }
.detail-head h2 { margin: 0; color: var(--color-text); font-size: 1.5rem; font-weight: 600; line-height: 1.38; letter-spacing: -0.024em; text-wrap: pretty; }
.source-row { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); padding-bottom: var(--space-5); border-bottom: 1px solid var(--color-border); }
.source-name { min-width: 0; overflow: hidden; color: var(--color-text-muted); font-size: 0.84rem; text-overflow: ellipsis; white-space: nowrap; }
.open-source { flex: 0 0 auto; min-height: 34px; display: inline-flex; align-items: center; gap: 6px; padding: 0 var(--space-4); color: var(--color-text-inverse); background: var(--color-text); border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 500; transition: opacity 160ms ease; }
.open-source svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; transition: transform 280ms var(--ease-spring); }
.open-source:hover:not(:disabled) svg { transform: translateX(3px); }
.open-source:disabled { cursor: progress; opacity: 0.6; }
.action-error { margin: 0; color: var(--color-danger); font-size: 0.84rem; }
.explanation-missing { margin: 0; color: var(--color-text-muted); font-size: 0.88rem; }
.detail-meta { margin-top: auto; display: flex; align-items: center; justify-content: space-between; color: var(--color-text-subtle); font-size: 0.78rem; }
.delete-link { padding: 0; color: var(--color-text-subtle); background: transparent; border: 0; cursor: pointer; font: inherit; font-size: 0.8rem; transition: color 160ms ease; }
.delete-link:hover { color: var(--color-danger); }

.detail-swap-enter-active { transition: opacity 260ms ease-out, transform 380ms var(--ease-spring); }
.detail-swap-leave-active { transition: opacity 120ms ease-in; }
.detail-swap-enter-from { opacity: 0; transform: translateY(6px); }
.detail-swap-leave-to { opacity: 0; }

button:focus-visible, input:focus-visible { outline: 2px solid var(--color-brand-700); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .detail-swap-enter-active, .detail-swap-leave-active, .sentence-row, .open-source svg, .sentence-search { transition: none; }
  .detail-swap-enter-from { transform: none; }
}
@media (max-width: 768px) {
  .sentence-toolbar { flex-direction: column; align-items: stretch; }
  .sentence-search { width: 100%; }
  .sentence-count { align-self: flex-end; }
  .sentence-notebook { height: auto; }
  .sentence-content { height: auto; display: block; }
  .sentence-list { max-height: 300px; padding: var(--space-2); border-right: 0; border-bottom: 1px solid var(--color-border); }
  .sentence-detail { padding: var(--space-6) 0; gap: var(--space-6); overflow: visible; }
  .detail-head h2 { font-size: 1.25rem; }
  .source-row { flex-direction: column; align-items: stretch; }
  .open-source { justify-content: center; }
}
</style>
